import { iconFor } from '../lib/icons';
import type { CurrentConditions as Current, DailyEntry } from '../types';

export default function CurrentConditions({
  current,
  today,
}: {
  current: Current | null;
  today: DailyEntry | undefined;
}) {
  if (!current) {
    return (
      <section className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
        <div className="opacity-80">No current observation available.</div>
      </section>
    );
  }
  // PWS has no "condition" text — fall back to today's forecast narrative
  // (it's day-granularity but better than blank).
  const conditionText =
    current.condition ||
    (today?.condition ?? '') ||
    (today?.narrative ? today.narrative.split('.')[0] : '');
  const Icon = iconFor(conditionText);
  // Both on-farm stations (Ambient PWS and LoRa) have no "condition" text of
  // their own — the forecast-narrative fallback above and the extras grid
  // below apply to either.
  const isStation = current.source === 'pws' || current.source === 'lora';
  const hasPwsExtras =
    isStation &&
    (current.uvIndex != null ||
      current.solarWm2 != null ||
      current.pressureInHg != null ||
      current.rainTodayIn != null);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-6">
        <Icon className="w-24 h-24 sm:w-32 sm:h-32 stroke-1" />
        <div>
          <div className="text-6xl sm:text-7xl font-thin tabular-nums">
            {current.tempF !== null ? `${current.tempF}°` : '—'}
          </div>
          <div className="text-lg opacity-90">{conditionText}</div>
          {today && (
            <div className="text-sm opacity-75">
              High {today.hi ?? '—'}° · Low {today.lo ?? '—'}°
              {current.feelsLikeF != null && (
                <span className="ml-2 opacity-80">· Feels {current.feelsLikeF}°</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Stat
          label="Wind"
          value={
            current.windMph !== null
              ? `${current.windMph} mph ${current.windDir ?? ''}`.trim()
              : '—'
          }
          sub={current.gustMph != null ? `gust ${current.gustMph}` : undefined}
        />
        <Stat
          label="Humidity"
          value={current.humidity !== null ? `${current.humidity}%` : '—'}
        />
        <Stat
          label="Dew point"
          value={current.dewpointF !== null ? `${current.dewpointF}°` : '—'}
        />
        <Stat
          label="Today"
          value={today?.narrative ? today.narrative.split('. ')[0] : '—'}
        />
      </div>

      {hasPwsExtras && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          <Stat
            label="UV"
            value={current.uvIndex != null ? String(current.uvIndex) : '—'}
            sub={uvBand(current.uvIndex)}
          />
          <Stat
            label="Solar"
            value={current.solarWm2 != null ? `${Math.round(current.solarWm2)}` : '—'}
            sub="W/m²"
          />
          <Stat
            label="Pressure"
            value={current.pressureInHg != null ? current.pressureInHg.toFixed(2) : '—'}
            sub="inHg"
          />
          <Stat
            label="Rain today"
            value={current.rainTodayIn != null ? `${current.rainTodayIn.toFixed(2)}"` : '—'}
            sub={
              current.rainWeekIn != null ? `${current.rainWeekIn.toFixed(2)}" this week` : undefined
            }
          />
        </div>
      )}

      <div className="text-[10px] opacity-50">{sourceLabel(current)}</div>
    </section>
  );
}

function sourceLabel(current: Current): string {
  if (current.source === 'pws') {
    return `From your PWS${current.stationName ? ` (${current.stationName})` : ''}`;
  }
  if (current.source === 'lora') {
    // stationName is "LoRa s2120-1" (or "LoRa station" with no device id) —
    // strip the leading "LoRa " so the parenthetical reads "(s2120-1)"; omit
    // it entirely if nothing meaningful is left.
    const deviceId = current.stationName?.replace(/^LoRa /, '').trim();
    const base = `From your LoRa station${deviceId && deviceId !== 'station' ? ` (${deviceId})` : ''}`;
    if (current.stale) {
      return current.ageMinutes != null
        ? `${base} · no report for ${current.ageMinutes} min`
        : `${base} · no recent report`;
    }
    return base;
  }
  if (current.source === 'nws-obs') return 'NWS station observation';
  return '';
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2.5">
      <div className="text-[10px] uppercase tracking-wider opacity-60">{label}</div>
      <div className="font-medium truncate">{value}</div>
      {sub && <div className="text-[10px] opacity-60 truncate">{sub}</div>}
    </div>
  );
}

function uvBand(uv: number | null): string | undefined {
  if (uv == null) return undefined;
  if (uv < 3) return 'low';
  if (uv < 6) return 'moderate';
  if (uv < 8) return 'high';
  if (uv < 11) return 'very high';
  return 'extreme';
}
